import { GenerativeMusicEngine } from './generative';
import { store } from '../app/store';

export interface PlaylistTrack {
  id: string;
  title: string;
  artist: string;
  file: string; // URL or local object URL
  duration?: number;
  language?: string;
  moods?: string[];
  scenes?: string[];
  timeOfDay?: string[];
  license?: string;
  sourceUrl?: string;
}

export class AudioPlayer {
  public generative: GenerativeMusicEngine;
  private audioElement: HTMLAudioElement;
  private playlist: PlaylistTrack[] = [];
  private currentTrackIndex: number = -1;
  private isUserUnlocked: boolean = false;
  private sleepTimerId: number | null = null;
  private sleepEndTime: number | null = null;
  private onTrackChangeCallbacks: ((track: PlaylistTrack | null) => void)[] = [];

  constructor() {
    this.generative = new GenerativeMusicEngine();

    this.audioElement = new Audio();
    this.audioElement.preload = 'metadata';

    this.audioElement.addEventListener('ended', this.onTrackEnded);
    this.audioElement.addEventListener('error', this.onTrackError);

    // Sync volume from store
    const state = store.getState();
    this.setVolume(state.audioVolume);
    this.setMute(state.audioMuted);

    this.setupMediaSession();
  }

  public async unlock(): Promise<void> {
    if (this.isUserUnlocked) return;
    this.isUserUnlocked = true;

    // Initialize generative engine on first gesture
    this.generative.init();

    // Unlock HTMLAudioElement by playing a tiny muted tick
    try {
      this.audioElement.src = 'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA';
      await this.audioElement.play();
      this.audioElement.pause();
    } catch {
      // Ignored
    }
  }

  public loadPlaylist(tracks: PlaylistTrack[]): void {
    this.playlist = tracks;
    if (this.playlist.length > 0 && this.currentTrackIndex === -1) {
      this.currentTrackIndex = 0;
    }
  }

  public addLocalSongs(files: FileList): void {
    const newTracks: PlaylistTrack[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const objectUrl = URL.createObjectURL(file);
      // Clean up filename for title
      const title = file.name.replace(/\.[^/.]+$/, '');
      newTracks.push({
        id: `local-${Date.now()}-${i}`,
        title,
        artist: 'Nhạc trên máy của bạn (Local)',
        file: objectUrl,
        moods: ['custom'],
        license: 'Local File',
      });
    }

    this.playlist.push(...newTracks);
    if (this.currentTrackIndex === -1) {
      this.currentTrackIndex = 0;
      this.playTrack(0);
    }
  }

  public async togglePlay(): Promise<void> {
    await this.unlock();
    const state = store.getState();

    if (state.audioPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  public play(): void {
    this.unlock();
    store.setState({ audioPlaying: true, audioEnabled: true });

    if (this.playlist.length > 0 && this.currentTrackIndex >= 0) {
      this.generative.stop();
      this.playTrack(this.currentTrackIndex);
    } else {
      // Fallback to Generative Web Audio Engine
      this.audioElement.pause();
      const state = store.getState();
      this.generative.setMood(state.timeOfDay, state.weather);
      this.generative.setVolume(state.audioVolume / 100);
      this.generative.setMute(state.audioMuted);
      this.generative.start();

      store.setState({
        currentTrackTitle: 'Khúc Mộng Vân Nam (Sáo & Cổ Tranh)',
        currentTrackMood: 'Ngũ cung tĩnh tâm',
      });
    }
  }

  public pause(): void {
    store.setState({ audioPlaying: false });
    this.audioElement.pause();
    this.generative.stop();
  }

  public playNext(): void {
    if (this.playlist.length === 0) return;
    this.currentTrackIndex = (this.currentTrackIndex + 1) % this.playlist.length;
    this.playTrack(this.currentTrackIndex);
  }

  public playPrev(): void {
    if (this.playlist.length === 0) return;
    this.currentTrackIndex = (this.currentTrackIndex - 1 + this.playlist.length) % this.playlist.length;
    this.playTrack(this.currentTrackIndex);
  }

  private playTrack(index: number): void {
    if (index < 0 || index >= this.playlist.length) return;
    const track = this.playlist[index];
    this.audioElement.src = track.file;
    this.audioElement.currentTime = 0;
    this.audioElement.play().catch((e) => {
      console.warn('Audio play prevented or file missing, skipping:', e);
      this.onTrackError();
    });

    store.setState({
      audioPlaying: true,
      currentTrackTitle: track.title,
      currentTrackMood: track.artist || 'Mộng Vân Nam',
    });

    this.updateMediaSession(track);
    for (const cb of this.onTrackChangeCallbacks) {
      cb(track);
    }
  }

  private onTrackEnded = (): void => {
    const state = store.getState();
    if (state.audioRepeatOne) {
      this.audioElement.currentTime = 0;
      this.audioElement.play();
    } else if (state.audioLoop || this.currentTrackIndex < this.playlist.length - 1) {
      this.playNext();
    } else {
      this.pause();
    }
  };

  private onTrackError = (): void => {
    console.warn('Track failed to play. Skipping to next in 1s...');
    setTimeout(() => {
      if (this.playlist.length > 1) {
        this.playNext();
      } else {
        // Fall back to generative
        this.generative.start();
      }
    }, 1000);
  };

  public setVolume(vol: number): void {
    // vol: 0..100
    const clamped = Math.max(0, Math.min(100, vol));
    this.audioElement.volume = clamped / 100;
    this.generative.setVolume(clamped / 100);
    store.setState({ audioVolume: clamped });
  }

  public setMute(muted: boolean): void {
    this.audioElement.muted = muted;
    this.generative.setMute(muted);
    store.setState({ audioMuted: muted });
  }

  public getRemainingSleepSeconds(): number | null {
    if (!this.sleepEndTime) return null;
    return Math.max(0, Math.round((this.sleepEndTime - Date.now()) / 1000));
  }

  public setSleepTimer(minutes: number | null): void {
    if (this.sleepTimerId !== null) {
      window.clearTimeout(this.sleepTimerId);
      this.sleepTimerId = null;
      this.sleepEndTime = null;
    }

    store.setState({ sleepTimerMinutes: minutes });

    if (minutes && minutes > 0) {
      const ms = minutes * 60 * 1000;
      this.sleepEndTime = Date.now() + ms;

      // Check fade-out during last 10 seconds
      this.sleepTimerId = window.setTimeout(() => {
        this.fadeOutAndStop();
      }, Math.max(100, ms - 10000));
    }
  }

  private fadeOutAndStop(): void {
    const startVol = store.getState().audioVolume;
    let fadeStep = 0;
    const interval = window.setInterval(() => {
      fadeStep++;
      const current = Math.max(0, startVol * (1 - fadeStep / 10));
      this.setVolume(current);

      if (fadeStep >= 10) {
        window.clearInterval(interval);
        this.pause();
        this.setVolume(startVol); // restore original volume setting
        this.setSleepTimer(null);
      }
    }, 1000);
  }

  private setupMediaSession(): void {
    if (!('mediaSession' in navigator)) return;

    navigator.mediaSession.setActionHandler('play', () => this.play());
    navigator.mediaSession.setActionHandler('pause', () => this.pause());
    navigator.mediaSession.setActionHandler('previoustrack', () => this.playPrev());
    navigator.mediaSession.setActionHandler('nexttrack', () => this.playNext());
  }

  private updateMediaSession(track: PlaylistTrack): void {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title,
      artist: track.artist,
      album: 'Yunnan Dreamscape',
      artwork: [{ src: './favicon.svg', sizes: '64x64', type: 'image/svg+xml' }],
    });
  }

  public onTrackChange(cb: (track: PlaylistTrack | null) => void): () => void {
    this.onTrackChangeCallbacks.push(cb);
    return () => {
      this.onTrackChangeCallbacks = this.onTrackChangeCallbacks.filter((c) => c !== cb);
    };
  }

  public getCurrentTrack(): PlaylistTrack | null {
    if (this.playlist.length > 0 && this.currentTrackIndex >= 0) {
      return this.playlist[this.currentTrackIndex];
    }
    return null;
  }
}
